import {
  FilledInputProps,
  InputProps,
  OutlinedInputProps,
  TextField,
} from '@mui/material';
import { useEffect, useRef } from 'react';
import { getClosestDistrict } from './utils';
import { getGovernorateForDistrict } from '../../../data/regions';

interface AddressWithAutocompleteProps {
  inputRef: React.RefObject<HTMLInputElement>;
  overrideAddress?: (address: string) => void;
  callback: (
    address: string,
    district: string,
    governorate: string,
    url: string,
    place_id: string
  ) => void;
}

const AddressWithAutocomplete = ({
  inputRef,
  callback,
  overrideAddress,
}: AddressWithAutocompleteProps) => {
  const autocomplete = useRef<google.maps.places.Autocomplete | null>();

  useEffect(() => {
    if (!inputRef.current || !window.google) return;

    const input = inputRef.current;
    let initialized = false;

    const handleInput = () => {
      const value = input.value;

      overrideAddress && overrideAddress(input.value)
      if (value.length >= 3 && !initialized) {
        initialized = true;

        if (autocomplete.current == null)
          autocomplete.current = new window.google.maps.places.Autocomplete(input, {
          componentRestrictions: {
            country: 'lb',
          },
        });

        autocomplete.current.addListener('place_changed', () => {
          const selectedPlace = autocomplete.current!.getPlace();
          const address: string = inputRef.current?.value ?? '';
          let district = '';
          let governorate = '';
          const url = selectedPlace.url ?? '';
          const place_id = selectedPlace.place_id ?? '';

          try {
            const addressComponents = selectedPlace.address_components;
            if (
              addressComponents &&
              addressComponents.filter((item) =>
                item.types.includes('administrative_area_level_2')
              )
            ) {
              const districtComponents = addressComponents.filter((item) =>
                item.types.includes('administrative_area_level_2')
              );
              district = getClosestDistrict(districtComponents[0].short_name);
              governorate = getGovernorateForDistrict(district);
            }
          } catch (e) {
            console.log('Failed to guess governorate');
            callback(address, '', '', url, place_id);
            return;
          }

          callback(address, district, governorate, url, place_id);
        });
      }
    };

    input.addEventListener('input', handleInput);

    return () => {
      input.removeEventListener('input', handleInput);
    };
  }, [inputRef, callback]);

  return null;
};

interface AddressInputWithAutocompleteProps {
  callback: (
    address: string,
    district: string,
    governorate: string,
    url: string,
    place_id: string
  ) => void;
  initialAddress: string;
  overrideAddress?: (address: string) => void;
  label: string;
  disabled?: boolean;
  InputProps?:
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | Partial<InputProps>;
}

export default function AddressInputWithAutocomplete({
  callback,
  initialAddress,
  overrideAddress,
  label,
  disabled,
  InputProps,
}: AddressInputWithAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.value = initialAddress;
  }, [initialAddress]);

  return (
    <>
      <TextField
        autoComplete="off"
        inputRef={inputRef}
        fullWidth
        defaultValue={initialAddress}
        type="string"
        InputLabelProps={{ shrink: true }}
        variant="outlined"
        placeholder="Start typing address"
        label={label}
        required
        InputProps={InputProps}
        disabled={!!disabled}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      />
      <AddressWithAutocomplete inputRef={inputRef} callback={callback} overrideAddress={overrideAddress} />
    </>
  );
}
